import { useEffect, useState } from "react";
import { X, Minus, Plus, Trash2, Tag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createCheckoutSession, validatePromoCode } from "../lib/api";
import { toast } from "sonner";

const CartDrawer = () => {
  const {
    isOpen,
    closeCart,
    enrichedItems,
    subtotal,
    discountAmount,
    promo,
    applyPromo,
    clearPromo,
    updateQty,
    removeItem,
    MAX_PER_PURCHASE,
  } = useCart();
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeCart();
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, closeCart]);

  const handleCheckout = async () => {
    if (enrichedItems.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: enrichedItems.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size || null })),
        promo_code: promo?.code || null,
      };
      const { url } = await createCheckoutSession(payload);
      if (!url) throw new Error("No checkout URL received");
      window.location.href = url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Unable to start checkout";
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const result = await validatePromoCode(code);
      if (!result.valid) {
        toast.error("That code isn't valid or has expired");
        return;
      }
      applyPromo({
        code: result.code,
        flash_price: result.flash_price,
        applies_to_categories: result.applies_to_categories,
        label: result.label,
      });
      toast.success(`Promo ${result.code} applied — ${result.label}`);
      setPromoInput("");
    } catch (e) {
      toast.error("Couldn't validate that promo code");
    } finally {
      setPromoBusy(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="cart-overlay"
        onClick={closeCart}
        className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity duration-500 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-50 h-[100dvh] max-h-screen w-full sm:w-[440px] bg-[#050505] border-l hairline transform transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between px-8 py-7 border-b hairline">
          <h3 className="font-serif text-2xl tracking-tight">Your Cart</h3>
          <button
            data-testid="close-cart-btn"
            onClick={closeCart}
            className="p-1 text-[var(--primary-text)]/70 hover:text-[var(--gold)] transition"
            aria-label="Close cart"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6 min-h-0">
          {enrichedItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty">
              <div className="divider-gold mb-6" />
              <p className="font-serif text-2xl mb-2">Your cart is empty</p>
              <p className="text-sm text-[var(--secondary-text)] uppercase tracking-label">
                Add a fragrance to begin
              </p>
            </div>
          ) : (
            <ul className="space-y-8">
              {enrichedItems.map((item) => (
                <li key={item.lineKey} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-20 h-24 bg-black border hairline flex items-center justify-center shrink-0 overflow-hidden">
                    <img
                      src={item.image || "/no1-ivory-bottle.jpg"}
                      alt={item.name}
                      className="max-h-full w-auto object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-serif text-lg leading-tight">{item.name}</p>
                        <p className="text-[10px] uppercase tracking-label text-[var(--secondary-text)] mt-1">
                          {item.tagline || "Eau de Parfum"} · {item.displaySize || item.size}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.product_id, item.size)}
                        data-testid={`remove-${item.product_id}-btn`}
                        className="text-[var(--secondary-text)] hover:text-[var(--gold)] transition"
                        aria-label="Remove item"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="inline-flex items-center">
                        <button
                          className="qty-btn"
                          data-testid={`cart-decrease-${item.product_id}-btn`}
                          onClick={() => updateQty(item.product_id, item.quantity - 1, item.size)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus size={14} />
                        </button>
                        <span
                          className="w-12 text-center text-sm tracking-label"
                          data-testid={`cart-qty-${item.product_id}`}
                        >
                          {item.quantity}
                        </span>
                        <button
                          className="qty-btn"
                          data-testid={`cart-increase-${item.product_id}-btn`}
                          onClick={() => updateQty(item.product_id, item.quantity + 1, item.size)}
                          disabled={item.quantity >= (item.maxQty || MAX_PER_PURCHASE)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="font-serif text-lg gold-text">
                        {item.discounted ? (
                          <>
                            <span className="text-[var(--secondary-text)] line-through text-sm mr-2">
                              ${item.baseLineTotal.toFixed(2)}
                            </span>
                            ${item.lineTotal.toFixed(2)}
                          </>
                        ) : (
                          <>${item.lineTotal.toFixed(2)}</>
                        )}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {enrichedItems.length > 0 && (
          <div className="border-t hairline px-8 py-7 space-y-4 shrink-0 pb-[calc(env(safe-area-inset-bottom)+28px)]">
            {/* Promo code */}
            <div data-testid="cart-promo-block">
              {promo ? (
                <div className="flex items-center justify-between border hairline px-4 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Tag size={14} className="text-[var(--accent-gold)] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-[var(--primary-text)] truncate" data-testid="cart-promo-code">
                        {promo.code}
                      </p>
                      <p className="text-[10px] uppercase tracking-label text-[var(--secondary-text)] truncate">
                        {promo.label}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { clearPromo(); toast("Promo removed"); }}
                    data-testid="cart-promo-remove-btn"
                    className="text-[10px] uppercase tracking-label text-[var(--secondary-text)] hover:text-[var(--gold)] transition"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") handleApplyPromo(); }}
                    placeholder="Promo code"
                    data-testid="cart-promo-input"
                    className="flex-1 bg-transparent border hairline px-4 py-3 text-sm tracking-label text-[var(--primary-text)] placeholder:text-[var(--secondary-text)] focus:outline-none focus:border-[var(--accent-gold)]/60 uppercase"
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={promoBusy || !promoInput.trim()}
                    data-testid="cart-promo-apply-btn"
                    className="btn-secondary text-xs px-5 disabled:opacity-50"
                  >
                    {promoBusy ? "…" : "Apply"}
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
                Subtotal
              </span>
              <span className="text-lg text-[var(--primary-text)]" data-testid="cart-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            {discountAmount > 0 && (
              <div className="flex items-center justify-between" data-testid="cart-discount-row">
                <span className="text-[11px] uppercase tracking-label gold-text">
                  Promo {promo?.code}
                </span>
                <span className="text-lg gold-text" data-testid="cart-discount">
                  −${discountAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
                Shipping
              </span>
              <span className="text-lg text-[var(--primary-text)]" data-testid="cart-shipping">
                $6.99
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
                Sales Tax (5.75%)
              </span>
              <span className="text-lg text-[var(--primary-text)]" data-testid="cart-tax">
                ${(subtotal * 0.0575).toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between border-t hairline pt-4">
              <span className="text-[11px] uppercase tracking-label text-[var(--accent-gold)]">
                Total
              </span>
              <span className="font-serif text-2xl gold-text" data-testid="cart-total">
                ${(subtotal + 6.99 + subtotal * 0.0575).toFixed(2)}
              </span>
            </div>
            <p className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
              Limit {MAX_PER_PURCHASE} bottles per order · Ships within United States
            </p>
            <button
              data-testid="checkout-btn"
              onClick={handleCheckout}
              disabled={loading}
              className="btn-secondary w-full"
            >
              {loading ? "Redirecting…" : "Proceed to Checkout"}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
